import { useRef } from "react";

import {
  Box,
  Button,
  IconButton,
  Menu,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ChatIcon,
  MenuArrowDownIcon,
  MessageIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useAccountType } from "@js/apps/common/hooks/use-account-type";
import { openSendSmsModal } from "@js/apps/freelancer/components/send-sms-modal";
import { useOpenMessengerModal } from "@js/apps/messenger/hooks/use-open-messenger-modal";
import type {
  FreelancerForNodeStaff,
  FreelancerPhoneNumber,
  FreelancerPublic,
} from "@js/types/freelancer";

type SendMessageButtonProps = {
  userId: number;
  profile: FreelancerPublic | FreelancerForNodeStaff;
};

export const SendMessageButton = (props: SendMessageButtonProps) => {
  const { isNodeStaff } = useAccountType();
  if (!isNodeStaff) {
    return <SendMessageButtonDefault {...props} />;
  }

  return <SendMessageWithSmsButton {...props} />;
};

const SendMessageWithSmsButton = ({
  userId,
  profile,
}: SendMessageButtonProps) => {
  const menuAnchorRef = useRef<HTMLDivElement>(null);

  const freelancerId = profile.user.id;
  const phoneNumber = (profile as FreelancerForNodeStaff).phone_number;
  const { openMessengerModal } = useOpenMessengerModal();

  const handleSendMessage = () => {
    void openMessengerModal({
      context: {
        participants: [freelancerId, userId],
      },
    });
  };

  return (
    <>
      <Button
        size="medium"
        variant="positive"
        shape="squared"
        sx={{ pr: "0!important" }}
        onClick={handleSendMessage}
        endIcon={
          <Box
            ref={menuAnchorRef}
            sx={{
              position: "relative",
              width: "48px",
              ml: 0.5,
              mr: 0.5,
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Menu
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              anchor={
                <IconButton
                  variant="transparent"
                  aria-label="Open send message menu"
                  shape="squared"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    transform: "translateY(-50%)",
                    right: 0,
                    height: "48px",
                    width: "48px",
                    p: "0 !important",
                    borderLeft: "1px solid var(--white)",
                    borderTopLeftRadius: "0 !important",
                    borderBottomLeftRadius: "0 !important",
                  }}
                >
                  <MenuArrowDownIcon sx={{ color: "var(--white)" }} />
                </IconButton>
              }
            >
              <Menu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendMessage();
                }}
                sx={{ p: 1, "&:hover, &:focus": { background: "#FAFAFA" } }}
              >
                <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      width: "36px",
                      height: "36px",
                      borderRadius: "6px",
                      backgroundColor: "#e7f5ed",
                    }}
                  >
                    <MessageIcon
                      sx={{ fontSize: "16px", color: "var(--positive-1)" }}
                    />
                  </Box>
                  <Stack>
                    <Typography
                      component="span"
                      fontWeight={500}
                      sx={{
                        fontSize: "14px !important",
                        lineHeight: "1.4 !important",
                      }}
                    >
                      Send a message
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "11px !important",
                        color: "var(--grey-2)",
                      }}
                    >
                      In their inbox + email notification
                    </Typography>
                  </Stack>
                </Stack>
              </Menu.Item>
              <Menu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  openSendSmsModal({
                    freelancer: profile as FreelancerForNodeStaff,
                    formattedPhone: formatPhoneNumber(phoneNumber),
                  });
                }}
                sx={{ p: 1, "&:hover, &:focus": { background: "#FAFAFA" } }}
                disabled={!phoneNumber?.phone_number}
              >
                <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      width: "36px",
                      height: "36px",
                      borderRadius: "6px",
                      backgroundColor: "var(--grey-4)",
                    }}
                  >
                    <ChatIcon
                      sx={{ fontSize: "16px", color: "var(--grey-1)" }}
                    />
                  </Box>
                  <Stack>
                    <Typography
                      component="span"
                      fontWeight={500}
                      sx={{
                        fontSize: "14px !important",
                        lineHeight: "1.4 !important",
                      }}
                    >
                      Send an SMS
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ fontSize: "11px !important" }}
                    >
                      {formatPhoneNumber(phoneNumber)}
                    </Typography>
                  </Stack>
                </Stack>
              </Menu.Item>
            </Menu>
          </Box>
        }
      >
        Send a message
      </Button>
    </>
  );
};

const hideCharacters = ({
  value,
  countToShow = 4,
}: {
  value: string;
  countToShow?: number;
}) => {
  if (!value) {
    return "*".repeat(countToShow);
  }
  const length = value.length;
  const count = length <= countToShow ? 0 : countToShow;
  const suffix = count ? value.slice(-count) : "";
  return `${"*".repeat(length - count)}${suffix}`;
};

export const formatPhoneNumber = (phoneNumber?: FreelancerPhoneNumber) => {
  if (!phoneNumber?.phone_number) {
    return "Phone number unavailable";
  }

  const country = SETTINGS.COUNTRY_ISO2_TO_COUNTRY_DATA.find(
    ({ code }) => code === phoneNumber.country_iso2,
  );
  if (!country) {
    return hideCharacters({ value: phoneNumber.phone_number_normalized });
  }

  return `+${country.dial_code} ${hideCharacters({ value: phoneNumber.phone_number })}`;
};

const SendMessageButtonDefault = ({
  userId,
  profile,
}: SendMessageButtonProps) => {
  const freelancerId = profile.user.id;
  const { openMessengerModal } = useOpenMessengerModal();

  return (
    <Button
      size="medium"
      variant="positive"
      shape="squared"
      onClick={() => {
        void openMessengerModal({
          context: {
            participants: [freelancerId, userId],
          },
        });
      }}
    >
      Send a message
    </Button>
  );
};
